import { useMemo, type Dispatch, type SetStateAction } from "react";
import { Dialog } from "@headlessui/react";
import { InformationCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";

import { IPart } from "../../../models/Part";
import Modal from "../../../layouts/Modal";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import { buildEbayTitleIntelligenceSuggestions } from "./ebayTitleIntelligence";

const EBAY_TITLE_MAX = 80;

type Props = {
  part: IPart;
  currentTitle: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onApplyTitle: (title: string) => void;
};

export default function EbayTitleIntelligenceModal({
  part,
  currentTitle,
  open,
  setOpen,
  onApplyTitle,
}: Props) {
  const suggestions = useMemo(
    () => buildEbayTitleIntelligenceSuggestions(part, currentTitle),
    [part, currentTitle]
  );

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-lg">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <span className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-700 ring-1 ring-inset ring-violet-200/80">
            <SparklesIcon className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <Dialog.Title className="text-base font-semibold text-slate-900">
              Title intelligence
            </Dialog.Title>
            <p className="mt-0.5 text-sm text-slate-600">
              eBay-style variants and tweaks. Suggestions are generated locally; swap
              in an API when available.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="shrink-0 self-start rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
        >
          Close
        </button>
      </div>

      <div className="mt-1 flex items-start gap-2 rounded-md border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-950">
        <InformationCircleIcon
          className="mt-0.5 h-4 w-4 flex-none text-amber-700"
          aria-hidden
        />
        <p>
          Use a strong front-loaded title (year, make, model, part type). Stay under
          {` ${EBAY_TITLE_MAX}`} characters; front-load the words buyers search for.
        </p>
      </div>

      <ul className="mt-4 max-h-[min(50vh,22rem)] space-y-2 overflow-y-auto pr-0.5">
        {suggestions.length === 0 ? (
          <li className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
            No alternative titles to suggest. Try editing the line above, then open
            again.
          </li>
        ) : (
          suggestions.map((s) => {
            const len = s.title.length;
            return (
              <li
                key={s.id}
                className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/80 p-3 shadow-sm ring-1 ring-slate-200/40"
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  {s.reason}
                </p>
                <p
                  className="mt-1.5 break-words font-mono text-sm leading-snug text-slate-900"
                  title={s.title}
                >
                  {s.title}
                </p>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <span
                    className={
                      len > EBAY_TITLE_MAX
                        ? "text-xs font-medium text-amber-700"
                        : "text-xs tabular-nums text-slate-500"
                    }
                  >
                    {len}/{EBAY_TITLE_MAX}
                  </span>
                  <ButtonInput
                    label="Use this title"
                    isSubmit={false}
                    onClick={() => {
                      onApplyTitle(s.title);
                      setOpen(false);
                    }}
                    classes="py-1.5 text-sm"
                  />
                </div>
              </li>
            );
          })
        )}
      </ul>
    </Modal>
  );
}
